import React, { useState, useEffect, useCallback } from 'react';
import { fetchMatterEmailRules, createMatterEmailRule, updateMatterEmailRule, deleteMatterEmailRule } from '../api';

function EmailRules({ authAxios, showFormMessage, cases = [] }) {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ case: '', match_type: 'SENDER', pattern: '' });

  const loadRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchMatterEmailRules(authAxios);
      setRules(data.results || data || []);
    } catch (err) {
      console.error('Error fetching email rules:', err);
    } finally {
      setIsLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleCreate = async () => {
    if (!formData.case || !formData.pattern.trim()) {
      showFormMessage('error', 'Case and pattern are required.');
      return;
    }
    try {
      await createMatterEmailRule(authAxios, {
        case: formData.case,
        match_type: formData.match_type,
        pattern: formData.pattern.trim(),
      });
      showFormMessage('success', 'Email rule created.');
      setShowForm(false);
      setFormData({ case: '', match_type: 'SENDER', pattern: '' });
      loadRules();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create rule.');
    }
  };

  const handleToggle = async (rule) => {
    try {
      await updateMatterEmailRule(authAxios, rule.id, { is_active: !rule.is_active });
      loadRules();
    } catch (err) {
      showFormMessage('error', 'Failed to update rule.');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this email rule?')) return;
    try {
      await deleteMatterEmailRule(authAxios, ruleId);
      showFormMessage('success', 'Rule deleted.');
      loadRules();
    } catch (err) {
      showFormMessage('error', 'Failed to delete rule.');
    }
  };

  const matchTypeLabels = { SENDER: 'Sender Email', DOMAIN: 'Sender Domain', SUBJECT: 'Subject Contains' };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Email Auto-Link Rules</h2>
          <p className="text-sm text-gray-500 mt-1">
            Incoming Outlook emails matching these rules will be automatically linked to the specified case.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-primary text-white text-sm rounded shadow hover:bg-primary-dark"
        >
          {showForm ? 'Cancel' : '+ New Rule'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-4 rounded shadow mb-6 border border-blue-200">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case</label>
              <select
                value={formData.case}
                onChange={(e) => setFormData(prev => ({ ...prev, case: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">— Select —</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.case_number} — {c.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Match Type</label>
              <select
                value={formData.match_type}
                onChange={(e) => setFormData(prev => ({ ...prev, match_type: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="SENDER">Sender Email</option>
                <option value="DOMAIN">Sender Domain</option>
                <option value="SUBJECT">Subject Contains</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pattern</label>
              <input
                type="text"
                value={formData.pattern}
                onChange={(e) => setFormData(prev => ({ ...prev, pattern: e.target.value }))}
                placeholder={formData.match_type === 'DOMAIN' ? 'e.g. lawfirm.co.bw' : formData.match_type === 'SUBJECT' ? 'e.g. Case REF-001' : 'e.g. client@email.com'}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={handleCreate} className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">
              Create Rule
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-gray-500 text-center py-12">Loading rules...</div>
      ) : rules.length === 0 ? (
        <div className="text-gray-500 text-center py-12">
          <p className="text-lg mb-2">No email rules configured</p>
          <p className="text-sm">Create rules to automatically link incoming emails to cases.</p>
        </div>
      ) : (
        <div className="bg-white rounded shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rules.map(rule => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium">{rule.case_number}</span>
                    {rule.case_title && <span className="text-gray-500 ml-1">— {rule.case_title}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{matchTypeLabels[rule.match_type] || rule.match_type}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{rule.pattern}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(rule)} className={`px-2 py-0.5 text-xs rounded-full ${rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(rule.id)} className="text-red-600 hover:text-red-800 text-sm">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EmailRules;
